import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import {Subscription, ReplaySubject, Observable, of, interval} from 'rxjs';
import { AppService } from '@irb/app.service';
import { UtilityService } from '@irb-common/services/utility.service';
import { IEvent, IrbSettingsEvents, IrbAppEvents } from '@irb-common/events';
import { SettingsPage } from '@irb/irb-settings/irb-settings.page';
import { IrbSettingsService } from '@irb/irb-settings/irb-settings.service';
import { IAlertDialogData } from "@irb/app.model";
import { IGlobalSettings, IGlobalSettingsSaveResponse } from '@irb/irb-settings/irb-settings.model';
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import { IDropdownItem } from '@irb-common/models/dropdown.model';

@Component({ standalone: false,
  selector: 'irb-settings-auto-trade',
  templateUrl: './settings-auto-trade.component.html',
  styleUrls: ['./settings-auto-trade.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class SettingsAutoTradeComponent implements SettingsPage<IGlobalSettings>, OnInit, OnDestroy {
  @ViewChild('automatedTradingTime') automatedTradingTime: DropdownFilterComponent;
  @ViewChild('automatedMasterAccount') automatedMasterAccount: DropdownFilterComponent;
  @ViewChild('automatedUserID') automatedUserID: DropdownFilterComponent;

  /* page */
  _globalSettings: IGlobalSettings;
  originalGlobalSettings: IGlobalSettings;
  _isModified: boolean;
  showUnsavedChangesWarning: boolean = false;
  defaultExpansionPanels:Array<string> = ["HISTORY","AUTOMATION_SETTINGS","MMC_AUTOMATION"];
  isAdmin: boolean = false;
  isAutomation: boolean = false;
  errors: Array<string>;
  errorCount: number = 0;
  isTradingPaused: boolean;
  isUiTest: boolean = false;
  checkSaveAutoTradeSub: Subscription;
  validationErrors: string[];
  validationErrorTemplates: TemplateRef<any>[];
  allowMMCPortApprovalPrevious = false;

  /* dropdowns */
  automatedTradingTimeOptions: Array<IDropdownItem>;
  automatedMasterAccountOptions: Array<IDropdownItem>;
  automatedTradingTimeSetting: IDropdownItem;
  automatedMasterAccountSetting: IDropdownItem;
  automatedUserIDOptions: Array<IDropdownItem> = [];
  automatedUserIDSetting: IDropdownItem;

  isValidAutoTradeUserId = true;
  allowMMCPortApproval: boolean;
  allowAutoRebalOnMMCUpdate: boolean;
  MMCAutomationEnabled: boolean;

  /* events */
  eventSubscription: Subscription;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  /* getter setter */
  get isModified() {
    return this._isModified;
  }

  set isModified(newIsModified: boolean) {
    this._isModified = newIsModified;
  }

  get globalSettings() {
    return this._globalSettings;    
  }

  set globalSettings(newGlobalSettings: IGlobalSettings) {
    this._globalSettings = newGlobalSettings;
  }

  /* */
  constructor(
    private as: AppService, 
    private util: UtilityService,
    private iss: IrbSettingsService,
    private cdr: ChangeDetectorRef) {
      this.eventSubscription = this.as.getEvents().subscribe((s) => { this.processEvent(s); }); 
  }

  ngOnInit(): void {
    this.MMCAutomationEnabled = this.as.getItem('IRB_APP_CONFIG')?.featureFlags.MMCAutomationEnabled;
    this.iss.loadExpansionState(this.defaultExpansionPanels.length, "settings-auto-trade" + this.defaultExpansionPanels.length, this.defaultExpansionPanels);
    this.iss.updateExpansionState('HISTORY','closed');
    this.iss.getAutoTradeUserIDDropDown().subscribe((data: any) => {
      this.loadAutoTradeUserIDDropDown(data);
    });
    
    this.iss.getTradeTimeDropDown().subscribe((data: any) => {
      this.loadAutomatedTradingTimeDropDown(data);

      this.iss.getAutoTradeMasterAccountDropDown().subscribe((data: any) => {
        this.loadAutoTradeMasterAccountDropDown(data);

        this.iss.getGlobalSettings().subscribe((data: any) => {
          this.originalGlobalSettings = this.util.clone(data);
          this.load(data as IGlobalSettings);
        });
      });
    });

    this.as.getUserDetails().subscribe((data:any) => {
        this.isAdmin = data.isAdmin;
        this.isAutomation = data.isAutomationEnabled;
        this.cdr.detectChanges();
    });
  }

  ngOnDestroy() {
    this.eventSubscription.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  processEvent(event: IEvent) {
    switch(event.type) {
      case IrbSettingsEvents.SAVE: 
        this.save();
        break;
      case IrbSettingsEvents.UNSAVED_CHANGES:
        if(this.isModified) {
          this.showUnsavedChangesWarning = true;
        }
        break;
      case IrbSettingsEvents.REVERT_CHANGES:
        this.revert();
        break;
    }
  }

  load(data: IGlobalSettings) {
    this.globalSettings = data;
    this.allowMMCPortApprovalPrevious = this.globalSettings.allowMMCPortApproval;
    this.isTradingPaused = (this.globalSettings.isTradingPaused === null) ? false : this.globalSettings.isTradingPaused;
    this.isModified = false;
    this.showUnsavedChangesWarning = false;
    this.as.fireEvent(IrbSettingsEvents.IS_NOT_MODIFIED);
    this.setInitialDropdownValues(data);
    this.allowMMCPortApproval = this.globalSettings.allowMMCPortApproval || false;
    this.allowAutoRebalOnMMCUpdate = this.globalSettings.allowAutoRebalOnMMCUpdate || false;
    if (!this.allowMMCPortApproval) {
      this.allowAutoRebalOnMMCUpdate = null;
    }
    this.cdr.detectChanges();
  }

  setInitialDropdownValues(data: IGlobalSettings) {
    this.automatedTradingTimeSetting = {
      name: data.tradeTime,
      value: data.tradeTime
    } as IDropdownItem;

    this.automatedMasterAccountSetting = {
      name: data.autoTradeMasterAccount,
      value: data.autoTradeMasterAccount
    } as IDropdownItem;

    this.automatedUserIDSetting = {
      name: data.autoTradeUserId,
      value: data.autoTradeUserId
    } as IDropdownItem;
    if (this.globalSettings) {
      this.globalSettings.tradeTime = this.automatedTradingTimeSetting.value;
      this.globalSettings.autoTradeMasterAccount = this.automatedMasterAccountSetting.value;
      this.globalSettings.autoTradeUserId = this.automatedUserIDSetting.value;
    }
  }

  onAutoRebalanceEnabledChange() {

    if (!this.globalSettings.isAutoRebalanceEnabled) {
      this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
        dTitle: 'Confirm',
        dCancelBtnLabel: "Cancel",
        msg: "Disabling automated rebalancing will delete all existing automated trading schedules for portfolios. Do you want to continue?",
        onProceed: () => {
          this.allowMMCPortApproval = false;
          this.allowMMCPortApprovalPrevious = this.globalSettings.allowMMCPortApproval;
          this.onMMCPortApprovalDisabled();
        },
        onCancel: () => {
          this.globalSettings.isAutoRebalanceEnabled = true;
        }
      } as IAlertDialogData);
    } else {
      if (this.allowMMCPortApprovalPrevious) {
        this.globalSettings.allowMMCPortApproval = this.allowMMCPortApprovalPrevious;
        this.onMMCPortApprovalEnabled();
      }
      this.setInitialDropdownValues(this.originalGlobalSettings);
      this.isTradingPaused = false;
      this.globalSettings.isTradingPaused = false;
      this.onInputChange();
    }
  }

  onTradingPausedChange() {
    if(this.globalSettings.isTradingPaused !== this.isTradingPaused) {
      if(!this.globalSettings.isTradingPaused) {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
          dTitle: 'Pause Orders for all Accounts?',
          dCancelBtnLabel: "Cancel",
          dProceedBtnLabel: "Yes, pause orders",
          msg: "When paused, any pending order on the blotter will not be submitted.",
          onProceed: () => {
            this.globalSettings.isTradingPaused = this.isTradingPaused;
            this.onInputChange();
          },
          onCancel: () => {
            this.isTradingPaused = false;
            this.globalSettings.isTradingPaused = false;
          }
        } as IAlertDialogData);
      } else {
        this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
          dTitle: 'Resume Orders for all Accounts?',
          dCancelBtnLabel: "Cancel",
          dProceedBtnLabel: "Yes, resume orders",
          msg: "Automated rebalances will resume on the next market day.",
          onProceed: () => {
            this.globalSettings.isTradingPaused = this.isTradingPaused;
            this.onInputChange();
          },
          onCancel: () => {
            this.isTradingPaused = true;
            this.globalSettings.isTradingPaused = true;
          }
        } as IAlertDialogData);
      }
    }
  }
  onMMCPortApprovalEnabled() {
    this.as.fireEvent(IrbAppEvents.SHOW_CONFIRMATION, {
      dTitle: 'Allow Automated Rebalancing for All Subscribed Models?',
      dCancelBtnLabel: "No, cancel",
      dProceedBtnLabel: "Yes, Allow for All",
      msg: "This will enable automated rebalancing for all of your currently subscribed Model Market Center models. You can pause automated rebalancing for individual models in the model’s settings.",
      onProceed: () => {
        this.allowMMCPortApproval = true;
        this.globalSettings.allowMMCPortApproval = this.allowMMCPortApproval;
        this.globalSettings.allowAutoRebalOnMMCUpdate = false;
        this.allowAutoRebalOnMMCUpdate = false;
        this.onInputChange();
      },
      onCancel: () => {
        this.onMMCPortApprovalDisabled();
      }
    } as IAlertDialogData);
  }
  onMMCPortApprovalDisabled() {
    this.allowMMCPortApproval = false;
    this.globalSettings.allowMMCPortApproval = this.allowMMCPortApproval;
    this.globalSettings.allowAutoRebalOnMMCUpdate = false;
    this.allowAutoRebalOnMMCUpdate = null;
    this.onInputChange();
  }
  onAllowAutoRebalOnMMCUpdateChange() {
    this.globalSettings.allowAutoRebalOnMMCUpdate = this.allowAutoRebalOnMMCUpdate;
    this.onInputChange();
  }

  onInputChange() {
    this.isModified = !this.util.compare(this.originalGlobalSettings,this.globalSettings);
    this.as.fireEvent((this.isModified) ? IrbSettingsEvents.IS_MODIFIED : IrbSettingsEvents.IS_NOT_MODIFIED);
    this.showUnsavedChangesWarning = false;

    this.validationErrors= new Array<string>();
    this.validationErrorTemplates = [];

    if(!this.globalSettings.isAutoRebalanceEnabled) {
      this.automatedTradingTimeSetting = {
        name: null,
        value: null
      } as IDropdownItem;
      this.automatedMasterAccountSetting = {
        name: null,
        value: null
      } as IDropdownItem;

      this.automatedUserIDSetting = {
        name: null,
        value: null
      } as IDropdownItem;
      this.validateTradingTimeControl();
      this.validateMasterAccountControl();
      this.validateUserIDControl();
    }
  }

  validateInput(): Observable<string[]> {
    this.validationErrors= new Array<string>();
    this.validationErrorTemplates = [];

    if (this.globalSettings.isAutoRebalanceEnabled) {
      this.validateTradingTimeControl();
      this.validateMasterAccountControl();
      this.validateUserIDControl();
    }

    return of(this.validationErrors);
  }

  validateUserIDControl() {
    if (this.globalSettings.isAutoRebalanceEnabled && !this.globalSettings.autoTradeUserId) {
      this.automatedUserID && this.automatedUserID.input.nativeElement.classList.remove("ng-valid");
      this.automatedUserID && this.automatedUserID.input.nativeElement.classList.add('ng-invalid');
      this.validationErrors.push('User ID cannot be empty.');
    } else {
      this.automatedUserID && this.automatedUserID.input.nativeElement.classList.remove("ng-invalid");
      this.automatedUserID && this.automatedUserID.input.nativeElement.classList.add('ng-valid');
    }
  }

  validateMasterAccountControl() {
    if (this.globalSettings.isAutoRebalanceEnabled && !this.globalSettings.autoTradeMasterAccount) {
      this.automatedMasterAccount && this.automatedMasterAccount.input.nativeElement.classList.remove("ng-valid");
      this.automatedMasterAccount && this.automatedMasterAccount.input.nativeElement.classList.add('ng-invalid');
      this.validationErrors.push('Master Account cannot be empty.');
    } else {
      this.automatedMasterAccount && this.automatedMasterAccount.input.nativeElement.classList.remove("ng-invalid");
      this.automatedMasterAccount && this.automatedMasterAccount.input.nativeElement.classList.add('ng-valid');
    }
  }

  validateTradingTimeControl() {
    if (this.globalSettings.isAutoRebalanceEnabled && !this.globalSettings.tradeTime) {
      this.automatedTradingTime && this.automatedTradingTime.input.nativeElement.classList.remove("ng-valid");
      this.automatedTradingTime && this.automatedTradingTime.input.nativeElement.classList.add('ng-invalid');
      this.validationErrors.push('Trading Time cannot be empty.');
    } else {
      this.automatedTradingTime && this.automatedTradingTime.input.nativeElement.classList.remove("ng-invalid");
      this.automatedTradingTime && this.automatedTradingTime.input.nativeElement.classList.add('ng-valid');
    }
  }

  save() {
    this.validateInput().subscribe(validationErrors => {
      if (validationErrors.length === 0) {
        if (!this.globalSettings.isAutoRebalanceEnabled) {
          this.globalSettings.tradeTime = null;
          this.globalSettings.autoTradeMasterAccount = null;
          this.globalSettings.autoTradeUserId = null;
        }
        this.as.fireEvent(IrbSettingsEvents.SAVE_START);
        
        this.iss.saveAutomatedTradeGlobalSettings(this.globalSettings).subscribe((data: any) => {
          const response = data as IGlobalSettingsSaveResponse;

          if (response.responseStatus === "SUCCESS") {
            if (response.responseUtilityMap?.statusId) {
              this.checkSaveAutomatedTradeAsyncStatus(response.responseUtilityMap.statusId);
            } else {
              this.saveAutomatedTradeComplete();
            }
          } else {
            this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
          }
        }, () => {
          this.as.fireEvent(IrbSettingsEvents.SAVE_ERROR);
        });
      } else {
        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
          dType: 'ERROR',
          dTitle: 'Errors Found',
          dProceedBtnLabel: '',
          msg: this.validationErrors.join('\n'),
          onProceed: () => {
          }
        } as IAlertDialogData);
      }
    });
  }  
  checkSaveAutomatedTradeAsyncStatus(importStatusId: string) {
    const intervalValue = this.isUiTest ? 0 : 3000;
    this.checkSaveAutoTradeSub = interval(intervalValue).subscribe(() => {
      this.iss.saveAutomatedTradeGlobalSettingsStatus(importStatusId).subscribe((response: any) => {
        if (response.responseUtilityMap?.reqStatus?.toUpperCase() === "COMPLETED") {
          this.checkSaveAutoTradeSub.unsubscribe();
          this.saveAutomatedTradeComplete();
        } else if(response.responseStatus !== "SUCCESS" ||
          (response.responseUtilityMap?.reqStatus && response.responseUtilityMap?.reqStatus?.toUpperCase() === "FAILED")) {
          this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: 'error',
            summary: 'Save Automated Trade Settings Failed',
            detail: "An error occurred processing your request."
          });
          this.checkSaveAutoTradeSub.unsubscribe();
          this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
        } else if(response.responseUtilityMap?.actionStatus?.reqStatus?.toUpperCase() === "IN_PROGRESS") {
        }
      }, () => {
        this.checkSaveAutoTradeSub.unsubscribe();
      });
    });
  }

  saveAutomatedTradeComplete() {
    this.iss.subscriptionTime = 0; // to enforce global settings to be re-read from the server
    this.iss.getGlobalSettings().subscribe((data: any) => {
      this.as.fireEvent(IrbSettingsEvents.SAVE_SUCCESS, "Automated trade settings were updated.");
      this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
      this.load(data as IGlobalSettings);
    });
  }
  revert() {
    if(this.isModified) {
      this.load(this.originalGlobalSettings);
    }
  }

  onAutomatedTradingTimeChange() {
    this.globalSettings.tradeTime = this.automatedTradingTimeSetting.value;
    this.validateTradingTimeControl();
    this.onInputChange();
  }

  onAutomatedMasterAccountChange() {
    this.globalSettings.autoTradeMasterAccount = this.automatedMasterAccountSetting.value;
    this.validateMasterAccountControl();
    this.onInputChange();
  }

  onAutomatedUserIDChange() {
    this.globalSettings.autoTradeUserId = this.automatedUserIDSetting.value;
    this.validateUserIDControl();
    this.onInputChange();
  }

  setAutomatedTradingTimeSetting(searchStr: any) {
    this.automatedTradingTime.set(DropdownFilterComponent.getFilteredList(searchStr, this.automatedTradingTimeOptions));
  }

  setAutomatedMasterAccountSetting(searchStr: any) {
    this.automatedMasterAccount.set(DropdownFilterComponent.getFilteredList(searchStr, this.automatedMasterAccountOptions));
  }

  setAutomatedUserIDSetting(searchStr: any) {
    this.automatedUserID.set(DropdownFilterComponent.getFilteredList(searchStr, this.automatedUserIDOptions));
  }

  loadAutomatedTradingTimeDropDown(data: any) {
    this.automatedTradingTimeOptions = [];

    if (data.responseData && Array.isArray(data.responseData)) {
      data.responseData.forEach((r: any) => {
        this.automatedTradingTimeOptions.push({
          name: r,
          value: r
        });
      });
    }
    this.cdr.detectChanges();
  }

  loadAutoTradeUserIDDropDown(data: any) {
    this.automatedUserIDOptions = [];

    if (data.responseData && Array.isArray(data.responseData)) {
      data.responseData.forEach((r: any) => {
        this.automatedUserIDOptions.push({
          name: r.name,
          value: r.name
        });
      });
    }
    this.cdr.detectChanges();
  }

  loadAutoTradeMasterAccountDropDown(data: any) {
    this.automatedMasterAccountOptions = [];
    if (data.responseData && Array.isArray(data.responseData)) {
      data.responseData.forEach((r: any) => {
        this.automatedMasterAccountOptions.push({
          name: r.name,
          value: r.name
        });
      });
    }
    this.cdr.detectChanges();
  }

  getIsTradingPausedDisabled() {
    if(!this.globalSettings.isAutoRebalanceEnabled) {
      return true;
    } else {
      if (this.globalSettings.isTradingPaused) { // to un-pause auto trading
        return !this.isAdmin || !this.isAutomation; //disabled if not admin or not automation. (to un-pause, both Admin and Automation roles should be enabled)
      } else { // to pause auto trading
        return !this.isAdmin && !this.isAutomation; // disabled if not admin and not automation. (to pause, at least one of the roles should be enabled)
      }
    }
  }
}
